/*
 * Regional layer of the synthetic model — demo only.
 *
 * Each state carries a handful of 0–1 traits (category affinity, price
 * sensitivity, health orientation, retailer footprint…). A state's KPIs are the
 * national prediction plus small trait-driven deltas that depend on the
 * selected options, so the map shows *where* a given stimulus resonates.
 */

import {
  AudienceId,
  Configuration,
  KpiId,
  Kpis,
  Region,
  RegionalReason,
  StateProfile,
  StateResult,
} from "./types";

/* ── State profiles ─────────────────────────────── */

type Traits = Omit<StateProfile, "abbr" | "id" | "name" | "region">;

/**
 * Each audience reads the same state through its own lens: Gen Z reacts more
 * to how young and price-sensitive a state is, parents to its family mix, and
 * so on. Amplifying those traits around the national middle shifts where the
 * configuration over- and under-indexes on the map.
 */
const AUDIENCE_LENS: Record<
  AudienceId,
  Partial<Record<keyof Traits, number>>
> = {
  all: {},
  genz: { priceSensitivity: 1.4, wholeFoods: 0.7, young: 2 },
  millennials: { health: 1.6, target: 1.5, wholeFoods: 1.4, young: 1.3 },
  nutellaBuyers: { nutella: 2, peanutButter: 0.6, priceSensitivity: 0.7 },
  parents: { family: 2, kroger: 1.3, walmart: 1.4, young: 0.5 },
  pbHeavy: { health: 1.3, nutella: 0.6, peanutButter: 2, walmart: 1.2 },
};

/** Pack design as a lean from Nutella-only (−1) to peanut-only (+1). */
const PACK_LEAN: Record<string, number> = {
  balanced: 0,
  nutellaLed: -0.5,
  nutellaOnly: -1,
  peanutLed: 0.5,
  peanutOnly: 1,
};

/** Census-division defaults; states override a few traits below. */
const DIVISIONS: Record<string, Traits> = {
  eastNorthCentral: {
    family: 0.5,
    health: 0.45,
    kroger: 0.8,
    nutella: 0.5,
    peanutButter: 0.65,
    priceSensitivity: 0.55,
    target: 0.7,
    walmart: 0.75,
    wholeFoods: 0.35,
    young: 0.45,
  },
  eastSouthCentral: {
    family: 0.55,
    health: 0.3,
    kroger: 0.7,
    nutella: 0.35,
    peanutButter: 0.8,
    priceSensitivity: 0.75,
    target: 0.35,
    walmart: 0.95,
    wholeFoods: 0.15,
    young: 0.45,
  },
  midAtlantic: {
    family: 0.45,
    health: 0.6,
    kroger: 0.1,
    nutella: 0.75,
    peanutButter: 0.45,
    priceSensitivity: 0.4,
    target: 0.6,
    walmart: 0.55,
    wholeFoods: 0.7,
    young: 0.5,
  },
  mountain: {
    family: 0.6,
    health: 0.6,
    kroger: 0.7,
    nutella: 0.45,
    peanutButter: 0.6,
    priceSensitivity: 0.5,
    target: 0.6,
    walmart: 0.75,
    wholeFoods: 0.4,
    young: 0.55,
  },
  newEngland: {
    family: 0.35,
    health: 0.75,
    kroger: 0,
    nutella: 0.7,
    peanutButter: 0.45,
    priceSensitivity: 0.35,
    target: 0.6,
    walmart: 0.5,
    wholeFoods: 0.7,
    young: 0.4,
  },
  pacific: {
    family: 0.45,
    health: 0.75,
    kroger: 0.6,
    nutella: 0.65,
    peanutButter: 0.5,
    priceSensitivity: 0.4,
    target: 0.65,
    walmart: 0.55,
    wholeFoods: 0.7,
    young: 0.55,
  },
  southAtlantic: {
    family: 0.5,
    health: 0.45,
    kroger: 0.6,
    nutella: 0.55,
    peanutButter: 0.7,
    priceSensitivity: 0.55,
    target: 0.55,
    walmart: 0.8,
    wholeFoods: 0.4,
    young: 0.5,
  },
  westNorthCentral: {
    family: 0.55,
    health: 0.45,
    kroger: 0.4,
    nutella: 0.4,
    peanutButter: 0.7,
    priceSensitivity: 0.55,
    target: 0.8,
    walmart: 0.8,
    wholeFoods: 0.2,
    young: 0.4,
  },
  westSouthCentral: {
    family: 0.6,
    health: 0.35,
    kroger: 0.6,
    nutella: 0.45,
    peanutButter: 0.75,
    priceSensitivity: 0.65,
    target: 0.55,
    walmart: 0.95,
    wholeFoods: 0.3,
    young: 0.55,
  },
};

const REGION_OF_DIVISION: Record<string, Exclude<Region, "all">> = {
  eastNorthCentral: "midwest",
  eastSouthCentral: "south",
  midAtlantic: "northeast",
  mountain: "west",
  newEngland: "northeast",
  pacific: "west",
  southAtlantic: "south",
  westNorthCentral: "midwest",
  westSouthCentral: "south",
};

const state = (
  id: string,
  abbr: string,
  name: string,
  division: keyof typeof DIVISIONS,
  overrides: Partial<Traits> = {}
): StateProfile => ({
  ...DIVISIONS[division],
  ...overrides,
  abbr,
  id,
  name,
  region: REGION_OF_DIVISION[division],
});

export const STATES: StateProfile[] = [
  state("01", "AL", "Alabama", "eastSouthCentral", { peanutButter: 0.85 }),
  state("04", "AZ", "Arizona", "mountain", { family: 0.55, young: 0.5 }),
  state("05", "AR", "Arkansas", "westSouthCentral", {
    priceSensitivity: 0.75,
    walmart: 1,
  }),
  state("06", "CA", "California", "pacific", { nutella: 0.75, young: 0.6 }),
  state("08", "CO", "Colorado", "mountain", { health: 0.9, nutella: 0.55 }),
  state("09", "CT", "Connecticut", "newEngland", { nutella: 0.75 }),
  state("10", "DE", "Delaware", "southAtlantic", { nutella: 0.6 }),
  state("11", "DC", "District of Columbia", "southAtlantic", {
    family: 0.25,
    health: 0.7,
    nutella: 0.7,
    walmart: 0.2,
    wholeFoods: 0.85,
    young: 0.75,
  }),
  state("12", "FL", "Florida", "southAtlantic", {
    family: 0.4,
    nutella: 0.65,
    walmart: 0.85,
    young: 0.45,
  }),
  state("13", "GA", "Georgia", "southAtlantic", {
    peanutButter: 0.8,
    young: 0.55,
  }),
  state("16", "ID", "Idaho", "mountain", { family: 0.8, health: 0.5 }),
  state("17", "IL", "Illinois", "eastNorthCentral", {
    nutella: 0.6,
    young: 0.5,
  }),
  state("18", "IN", "Indiana", "eastNorthCentral", {
    kroger: 0.9,
    peanutButter: 0.7,
  }),
  state("19", "IA", "Iowa", "westNorthCentral"),
  state("20", "KS", "Kansas", "westNorthCentral"),
  state("21", "KY", "Kentucky", "eastSouthCentral", { kroger: 0.9 }),
  state("22", "LA", "Louisiana", "westSouthCentral", {
    peanutButter: 0.7,
    priceSensitivity: 0.7,
  }),
  state("23", "ME", "Maine", "newEngland", {
    health: 0.65,
    priceSensitivity: 0.5,
  }),
  state("24", "MD", "Maryland", "southAtlantic", {
    nutella: 0.65,
    wholeFoods: 0.55,
  }),
  state("25", "MA", "Massachusetts", "newEngland", { health: 0.8 }),
  state("26", "MI", "Michigan", "eastNorthCentral", { kroger: 0.9 }),
  state("27", "MN", "Minnesota", "westNorthCentral", {
    health: 0.6,
    target: 0.95,
  }),
  state("28", "MS", "Mississippi", "eastSouthCentral", {
    priceSensitivity: 0.85,
  }),
  state("29", "MO", "Missouri", "westNorthCentral"),
  state("30", "MT", "Montana", "mountain", { kroger: 0.4 }),
  state("31", "NE", "Nebraska", "westNorthCentral"),
  state("32", "NV", "Nevada", "mountain", { health: 0.5, young: 0.55 }),
  state("33", "NH", "New Hampshire", "newEngland"),
  state("34", "NJ", "New Jersey", "midAtlantic", { nutella: 0.8 }),
  state("35", "NM", "New Mexico", "mountain", { priceSensitivity: 0.7 }),
  state("36", "NY", "New York", "midAtlantic", {
    nutella: 0.8,
    target: 0.5,
    walmart: 0.35,
    wholeFoods: 0.75,
  }),
  state("37", "NC", "North Carolina", "southAtlantic", { peanutButter: 0.8 }),
  state("38", "ND", "North Dakota", "westNorthCentral"),
  state("39", "OH", "Ohio", "eastNorthCentral", { kroger: 0.9 }),
  state("40", "OK", "Oklahoma", "westSouthCentral", { priceSensitivity: 0.7 }),
  state("41", "OR", "Oregon", "pacific", { health: 0.8, walmart: 0.5 }),
  state("42", "PA", "Pennsylvania", "midAtlantic", { peanutButter: 0.55 }),
  state("44", "RI", "Rhode Island", "newEngland"),
  state("45", "SC", "South Carolina", "southAtlantic", { peanutButter: 0.8 }),
  state("46", "SD", "South Dakota", "westNorthCentral"),
  state("47", "TN", "Tennessee", "eastSouthCentral", { peanutButter: 0.85 }),
  state("48", "TX", "Texas", "westSouthCentral", { family: 0.65, young: 0.6 }),
  state("49", "UT", "Utah", "mountain", { family: 0.9, young: 0.7 }),
  state("50", "VT", "Vermont", "newEngland", {
    health: 0.85,
    target: 0.2,
    walmart: 0.3,
    wholeFoods: 0.4,
  }),
  state("51", "VA", "Virginia", "southAtlantic", { health: 0.5, nutella: 0.6 }),
  state("53", "WA", "Washington", "pacific", { health: 0.8 }),
  state("54", "WV", "West Virginia", "southAtlantic", {
    health: 0.2,
    nutella: 0.25,
    priceSensitivity: 0.85,
  }),
  state("55", "WI", "Wisconsin", "eastNorthCentral", { peanutButter: 0.65 }),
  state("56", "WY", "Wyoming", "mountain", { kroger: 0.4 }),
];

export const STATE_BY_ID = new Map(STATES.map((s) => [s.id, s]));

/* ── Regional summary ───────────────────────────── */

const REGION_LABEL: Record<Exclude<Region, "all">, string> = {
  midwest: "the Midwest",
  northeast: "the Northeast",
  south: "the South",
  west: "the West",
};

/**
 * Two or three sentences on how the selected KPI differs across the country:
 * which regions lead and trail, and what drives the spread. `**text**` marks
 * terms to highlight.
 */
export const regionalSummary = (
  results: StateResult[],
  national: Kpis,
  kpi: KpiId,
  kpiLabel: string
): string => {
  if (results.length === 0) return "";
  const rows = results.map((r) => ({
    delta: r.kpis[kpi] - national[kpi],
    result: r,
  }));
  const spread =
    Math.max(...rows.map((r) => r.delta)) -
    Math.min(...rows.map((r) => r.delta));
  const fmt = (d: number) => {
    const abs = Math.abs(d).toFixed(kpi === "wtp" ? 2 : 1);
    const sign = d >= 0 ? "+" : "−";
    return kpi === "wtp"
      ? `${sign}$${abs}`
      : `${sign}${abs}\u00A0pt${abs === "1.0" ? "" : "s"}`;
  };
  const span =
    kpi === "wtp" ? `$${spread.toFixed(2)}` : `${spread.toFixed(1)}\u00A0pts`;

  // Regions ranked by average delta.
  const byRegion = new Map<Exclude<Region, "all">, number[]>();
  for (const row of rows) {
    const list = byRegion.get(row.result.state.region) ?? [];
    list.push(row.delta);
    byRegion.set(row.result.state.region, list);
  }
  const regions = [...byRegion.entries()]
    .map(([region, deltas]) => ({
      avg: deltas.reduce((a, b) => a + b, 0) / deltas.length,
      region,
    }))
    .sort((a, b) => b.avg - a.avg);
  const lead = regions[0];
  const trail = regions[regions.length - 1];
  const ranked = [...rows].sort((a, b) => b.delta - a.delta);
  const top = ranked.slice(0, 2).map((r) => r.result.state.name);
  const bottom = ranked
    .slice(-2)
    .reverse()
    .map((r) => r.result.state.name);

  // Most frequent drivers among over- and under-indexing states.
  const driver = (sign: 1 | -1) => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (Math.sign(row.delta) !== sign) continue;
      for (const reason of row.result.reasons) {
        if (Math.sign(reason.delta) !== sign) continue;
        counts.set(reason.label, (counts.get(reason.label) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  };
  const up = driver(1);
  const down = driver(-1);

  const tight = kpi === "wtp" ? spread < 0.15 : spread < 3;
  const first = tight
    ? `${kpiLabel} is **fairly uniform** across states (${span} between the best and worst state), so geography is not where this configuration wins or loses.`
    : `${kpiLabel} spans **${span}** between states. **${capital(REGION_LABEL[lead.region])}** leads (${fmt(lead.avg)} on average, strongest in ${top.join(" and ")}); **${REGION_LABEL[trail.region]}** trails (${fmt(trail.avg)}, weakest in ${bottom.join(" and ")}).`;
  const second = [
    up ? `Over-indexing states share one trait: **${lower(up)}**.` : null,
    down
      ? `Where it under-indexes, the common factor is **${lower(down)}**.`
      : null,
  ]
    .filter((x): x is string => x !== null)
    .join(" ");
  return [first, second].filter(Boolean).join(" ");
};

const capital = (label: string) =>
  label.charAt(0).toUpperCase() + label.slice(1);

const lower = (label: string) => label.charAt(0).toLowerCase() + label.slice(1);

/* ── Regional prediction ────────────────────────── */

/** KPIs for every state, given the audience prediction of the configuration. */
export const stateResults = (
  configuration: Configuration,
  national: Kpis,
  audience: AudienceId = "all"
): StateResult[] => {
  const perState = STATES.map((profile) =>
    regionalTerms(audienceProfile(profile, audience), configuration)
  );
  // Centre every term across states so the national figure is the average
  // state: some states above it, some below, none systematically shifted.
  const termCount = perState[0].length;
  const means = Array.from({ length: termCount }, (_, i) => {
    const sum = (pick: (t: Term) => number) =>
      perState.reduce((acc, terms) => acc + pick(terms[i]), 0) /
      perState.length;
    return {
      awareness: sum((t) => t.awareness ?? 0),
      brandFit: sum((t) => t.brandFit ?? 0),
      pi: sum((t) => t.pi),
      trial: sum((t) => t.trial ?? t.pi * 0.5),
      wtp: sum((t) => t.wtp ?? 0),
    };
  });

  return STATES.map((profile, s) => {
    const terms = perState[s].map((term, i) => {
      const pi = term.pi - means[i].pi;
      // The authored label matches the raw sign; flip it if centring did.
      const flipped = term.pi !== 0 && Math.sign(pi) !== Math.sign(term.pi);
      return {
        awareness: (term.awareness ?? 0) - means[i].awareness,
        brandFit: (term.brandFit ?? 0) - means[i].brandFit,
        label: flipped ? term.label.other : term.label.chosen,
        pi,
        trial: (term.trial ?? term.pi * 0.5) - means[i].trial,
        wtp: (term.wtp ?? 0) - means[i].wtp,
      };
    });
    const kpis: Kpis = { ...national };
    for (const term of terms) {
      kpis.purchaseIntent += term.pi;
      kpis.awareness += term.awareness;
      kpis.trial += term.trial;
      kpis.wtp += term.wtp;
      kpis.brandFit += term.brandFit;
      kpis.appeal += term.pi * 0.5;
    }
    const reasons: RegionalReason[] = terms
      .filter((term) => Math.abs(term.pi) >= 0.3)
      .sort((a, b) => Math.abs(b.pi) - Math.abs(a.pi))
      .slice(0, 3)
      .map((term) => ({ delta: term.pi, label: term.label }));
    return { kpis, reasons, state: profile };
  });
};

/* ── Private helpers ────────────────────────────── */

/** Reason label for the raw sign of a term, and the label for the opposite sign. */
interface Label {
  chosen: string;
  other: string;
}

interface Term {
  awareness?: number;
  brandFit?: number;
  label: Label;
  pi: number;
  trial?: number;
  wtp?: number;
}

const audienceProfile = (
  profile: StateProfile,
  audience: AudienceId
): StateProfile => {
  const lens = AUDIENCE_LENS[audience];
  const adjusted = { ...profile };
  for (const [trait, factor] of Object.entries(lens) as [
    keyof Traits,
    number,
  ][]) {
    adjusted[trait] = Math.max(
      0,
      Math.min(1, 0.5 + (profile[trait] - 0.5) * factor)
    );
  }
  return adjusted;
};

/** Label depending on the sign of an effect. */
const bySign = (value: number, positive: string, negative: string): Label =>
  value >= 0
    ? { chosen: positive, other: negative }
    : { chosen: negative, other: positive };

/** Label depending on whether a 0–1 trait is above the national middle. */
const byTrait = (trait: number, above: string, below: string): Label =>
  trait >= 0.5
    ? { chosen: above, other: below }
    : { chosen: below, other: above };

/** Signed trait deviation from the national middle, scaled. */
const dev = (trait: number, scale: number): number => (trait - 0.5) * scale;

const retailFootprint = (profile: StateProfile, retail: string): number => {
  switch (retail) {
    case "walmart":
      return profile.walmart;
    case "target":
      return profile.target;
    case "walmartTarget":
      return (
        Math.max(profile.walmart, profile.target) * 0.7 +
        Math.min(profile.walmart, profile.target) * 0.4
      );
    case "kroger":
      return profile.kroger;
    case "wholeFoods":
      return profile.wholeFoods;
    default:
      return 0.9;
  }
};

const regionalTerms = (
  profile: StateProfile,
  configuration: Configuration
): Term[] => {
  const terms: Term[] = [];
  const pb = profile.peanutButter;
  const nutella = profile.nutella;
  const price = profile.priceSensitivity;
  const health = profile.health;
  const family = profile.family;
  const young = profile.young;

  terms.push({
    brandFit: dev(nutella, 6),
    label: byTrait(
      pb,
      "High peanut butter category penetration",
      "Low peanut butter category penetration"
    ),
    pi: dev(pb, 6),
  });
  terms.push({
    awareness: dev(nutella, 6),
    label: byTrait(
      nutella,
      "High Nutella brand penetration",
      "Low Nutella brand penetration"
    ),
    pi: dev(nutella, 6),
    wtp: dev(nutella, 0.2),
  });

  const priceLabel = byTrait(
    price,
    "Price-sensitive shoppers",
    "Low price sensitivity"
  );
  switch (configuration.price) {
    case "4.99":
      terms.push({
        label: priceLabel,
        pi: dev(price, 6),
        wtp: -dev(price, 0.3),
      });
      break;
    case "5.49":
      terms.push({
        label: priceLabel,
        pi: dev(price, 3),
        wtp: -dev(price, 0.3),
      });
      break;
    case "6.49":
      terms.push({
        label: priceLabel,
        pi: -dev(price, 8),
        wtp: -dev(price, 0.3),
      });
      break;
    default:
      terms.push({
        label: priceLabel,
        pi: -dev(price, 2),
        wtp: -dev(price, 0.3),
      });
  }

  switch (configuration.positioning) {
    case "protein":
    case "natural":
      terms.push({
        label: byTrait(
          health,
          "Health-conscious buyers",
          "Less health-conscious buyers"
        ),
        pi: dev(health, 8),
        wtp: dev(health, 0.15),
      });
      break;
    case "indulgence":
      terms.push({
        label: byTrait(
          nutella,
          "Indulgence message resonates (strong Nutella affinity)",
          "Indulgence message under-delivers (weak Nutella affinity)"
        ),
        pi: dev(nutella, 6),
      });
      break;
    default:
      terms.push({
        label: byTrait(
          family,
          "Above-average share of households with children",
          "Below-average share of households with children"
        ),
        pi: dev(family, 6),
      });
  }

  switch (configuration.claim) {
    case "sourceOfProtein":
    case "noArtificial":
      terms.push({
        label: byTrait(
          health,
          "Health claim resonates locally",
          "Health claim resonates less locally"
        ),
        pi: dev(health, 4),
      });
      break;
    case "realPeanuts":
      terms.push({
        label: byTrait(
          pb,
          "“Real peanuts” claim resonates with category buyers",
          "“Real peanuts” claim resonates less locally"
        ),
        pi: dev(pb, 4),
      });
      break;
    default:
      terms.push({
        label: byTrait(
          family,
          "Breakfast claim fits family routines",
          "Breakfast claim fits fewer households"
        ),
        pi: dev(family, 3),
      });
  }

  // Pack design runs from Nutella-only (−1) to peanut-only (+1): the more the
  // pack leans peanut, the more it depends on local category habits rather
  // than on Nutella equity.
  const lean = PACK_LEAN[configuration.packaging] ?? 0;
  {
    const pi =
      dev(pb, 6) * Math.max(0, lean) +
      dev(nutella, 6) * Math.max(0, -lean) +
      (Math.abs(lean) < 0.01 ? dev(pb, 2) + dev(nutella, 2) : 0);
    terms.push({
      brandFit: dev(nutella, 3) * -lean,
      label:
        lean > 0
          ? bySign(
              pi,
              "Peanut-forward pack matches local category habits",
              "Peanut-forward pack mismatches local category habits"
            )
          : lean < 0
            ? bySign(
                pi,
                "Nutella-led pack leverages strong brand equity",
                "Nutella-led pack leverages limited brand equity"
              )
            : bySign(
                pi,
                "Co-branded pack matches local tastes",
                "Co-branded pack under-indexes locally"
              ),
      pi,
    });
  }

  if (configuration.packSize === "20") {
    terms.push({
      label: byTrait(
        family,
        "Large format fits larger households",
        "Large format fits fewer households"
      ),
      pi: dev(family, 4),
    });
  } else if (configuration.packSize === "12") {
    terms.push({
      label: byTrait(
        young,
        "Small format fits younger, smaller households",
        "Small format fits fewer households"
      ),
      pi: dev(young, 2),
    });
  }

  switch (configuration.advertising) {
    case "social":
    case "influencer":
      terms.push({
        awareness: dev(young, 6),
        label: byTrait(
          young,
          "Younger population, high social media reach",
          "Older population, lower social media reach"
        ),
        pi: dev(young, 5),
      });
      break;
    case "tv":
      terms.push({
        awareness: -dev(young, 5),
        label: byTrait(
          young,
          "TV reaches fewer younger buyers",
          "TV reaches an older audience well"
        ),
        pi: -dev(young, 4),
      });
      break;
    case "retailMedia":
      terms.push({
        awareness: dev(profile.walmart, 4),
        label: byTrait(
          profile.walmart,
          "Retail media backed by high Walmart traffic",
          "Retail media limited by low Walmart traffic"
        ),
        pi: dev(profile.walmart, 2),
      });
      break;
    default:
      // Mixed plans reach every state alike: no regional term.
      break;
  }

  const footprint = retailFootprint(profile, configuration.retail);
  terms.push({
    awareness: (footprint - 0.6) * 20,
    label: bySign(
      footprint - 0.6,
      "High store density for selected retailers",
      "Low store density for selected retailers"
    ),
    pi: (footprint - 0.6) * 8,
    trial: (footprint - 0.6) * 10,
  });

  return terms;
};
